import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Notification from 'utils/Notifications'
import DashboardTab from './tabs/DashboardTab'
import UsersTab from './tabs/UsersTab'
import RolesTab from './tabs/RolesTab'
import EnrollTab from './tabs/EnrollTab'
import StudentsTab from './tabs/StudentsTab'
import EmployeesTab from './tabs/EmployeesTab'
import SyllabusesTab from './tabs/SyllabusesTab'
import StatementsTab from './tabs/StatementsTab'
import AuditTab from './tabs/AuditTab'
import SettingsTab from './tabs/SettingsTab'
import ExternalLinkTab from './tabs/ExternalLinkTab'
import styles from './AdminFullPage.module.scss'

function AdminFullPage({ initialTab = 'dashboard' }) {
  const { t } = useTranslation('common')
  // Wave 4: title в табе браузера для админ-панели.
  useEffect(() => {
    document.title = `${t('admin.fullPage.title_admin', 'Админ')} — ЧУ`
  }, [t])
  const [active, setActive] = useState(initialTab)
  const TABS = useMemo(
    () => [
      { key: 'dashboard', label: t('admin.fullPage.tab_dashboard') },
      { key: 'users', label: t('admin.fullPage.tab_users') },
      { key: 'roles', label: t('admin.fullPage.tab_roles') },
      { key: 'enroll', label: t('admin.fullPage.tab_enroll') },
      { key: 'students', label: t('admin.fullPage.tab_students') },
      { key: 'employees', label: t('admin.fullPage.tab_employees') },
      { key: 'syllabuses', label: t('admin.fullPage.tab_syllabuses') },
      { key: 'schedule', label: t('admin.fullPage.tab_schedule') },
      { key: 'statements', label: t('admin.fullPage.tab_statements') },
      { key: 'accounting', label: t('admin.fullPage.tab_accounting') },
      { key: 'archive', label: t('admin.fullPage.tab_archive') },
      { key: 'audit', label: t('admin.fullPage.tab_audit') },
      { key: 'settings', label: t('admin.fullPage.tab_settings') },
      { key: 'integrations', label: t('admin.fullPage.tab_integrations') },
    ],
    [t],
  )
  const [notify, setNotify] = useState({
    isOpen: false,
    message: '',
    type: 'info',
    sound: '',
  })

  const showNotify = useCallback((message, type = 'info') => {
    setNotify({
      isOpen: true,
      message,
      type,
      sound:
        type === 'success'
          ? 'success'
          : type === 'error'
            ? 'error'
            : 'default',
    })
  }, [])

  const renderTab = () => {
    switch (active) {
      case 'dashboard':
        return <DashboardTab />
      case 'users':
        return <UsersTab notify={showNotify} />
      case 'roles':
        return <RolesTab notify={showNotify} />
      case 'enroll':
        return <EnrollTab notify={showNotify} />
      case 'students':
        return <StudentsTab notify={showNotify} />
      case 'employees':
        return <EmployeesTab />
      case 'syllabuses':
        return <SyllabusesTab />
      case 'schedule':
        return (
          <ExternalLinkTab
            title={t('admin.fullPage.schedule_title')}
            description={t('admin.fullPage.schedule_desc')}
            to="/schedule/"
            label={t('admin.fullPage.schedule_link')}
          />
        )
      case 'statements':
        return <StatementsTab notify={showNotify} />
      case 'accounting':
        return (
          <ExternalLinkTab
            title={t('admin.fullPage.accounting_title')}
            description={t('admin.fullPage.accounting_desc')}
            to="/accounting/"
            label={t('admin.fullPage.accounting_link')}
          />
        )
      case 'archive':
        return (
          <ExternalLinkTab
            title={t('admin.fullPage.archive_title')}
            description={t('admin.fullPage.archive_desc')}
            to="/archive/"
            label={t('admin.fullPage.archive_link')}
          />
        )
      case 'audit':
        return <AuditTab />
      case 'settings':
        return <SettingsTab notify={showNotify} />
      case 'integrations':
        return (
          <ExternalLinkTab
            title={t('admin.fullPage.integrations_title')}
            description={t('admin.fullPage.integrations_desc')}
            to="/admin/integrations/"
            label={t('admin.fullPage.integrations_link')}
          />
        )
      default:
        return null
    }
  }

  return (
    <div title={t('admin.fullPage.page_title')}>
      <div className={styles.wrap}>
        <aside className={styles.tabs}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${styles.tab} ${
                active === tab.key ? styles.tab_active : ''
              }`}
              onClick={() => setActive(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </aside>
        <main className={styles.content}>{renderTab()}</main>
      </div>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default AdminFullPage
